import type { ScopeSectionSchema } from './types'

export const sowSections: ScopeSectionSchema[] = [
  // 1. Legal Text
  {
    id: 'sow_legal_text',
    tabId: 'sow',
    formTitle: 'Legal Text',
    formDescription: 'Standard SOW preamble and scope of services template. Edit to customise for each client.',
    pdfTitle: 'Statement of Work',
    pdfDescription: 'SOW legal preamble and scope of services.',
    highLevelSummary: 'SOW legal text with preamble and standard scope of services clauses.',
    groups: [
      {
        id: 'sow_legal_text_group',
        tabId: 'sow',
        sectionId: 'sow_legal_text',
        formTitle: 'SOW Legal Text',
        pdfTitle: 'SOW Legal Text',
        pdfDescription: 'Standard SOW preamble and scope of services.',
      },
    ],
    fields: [
      {
        id: 'sow_legal_text_content',
        tabId: 'sow',
        sectionId: 'sow_legal_text',
        groupId: 'sow_legal_text_group',
        path: 'sowConfig.legalText',
        valueType: 'textarea',
        formLabel: 'SOW Legal Text',
        formDescription: 'Standard SOW preamble, scope of services, and out-of-scope clauses. Replace [Client Name] and [Insert Date] with actual values.',
        placeholder: 'This Statement of Work ("SOW") is issued under the terms of...',
        pdfLabel: 'Statement of Work',
        pdfDescription: 'SOW legal text.',
        contributesToCompletion: false,
      },
    ],
  },

  // 2. Delivery Notes (replaces Delivery Approach & Milestones)
  {
    id: 'sow_delivery_notes',
    tabId: 'sow',
    formTitle: 'Delivery Notes',
    formDescription: 'Delivery approach notes, assigned implementation and customer success leads.',
    pdfTitle: 'Delivery Notes',
    pdfDescription: 'Delivery approach and assigned personnel.',
    highLevelSummary: 'Delivery approach notes and assigned leads.',
    groups: [
      {
        id: 'sow_delivery_notes_group',
        tabId: 'sow',
        sectionId: 'sow_delivery_notes',
        formTitle: 'Delivery Information',
        pdfTitle: 'Delivery Information',
        pdfDescription: 'Delivery approach notes and assigned leads.',
      },
    ],
    fields: [
      {
        id: 'sow_delivery_notes_content',
        tabId: 'sow',
        sectionId: 'sow_delivery_notes',
        groupId: 'sow_delivery_notes_group',
        path: 'sowConfig.deliveryNotes',
        valueType: 'textarea',
        formLabel: 'Delivery Notes',
        formDescription: 'Describe how delivery will be conducted.',
        placeholder: 'e.g., Agile delivery with 2-week sprints, weekly status reports...',
        pdfLabel: 'Delivery Notes',
        pdfDescription: 'How delivery will be conducted.',
        contributesToCompletion: true,
      },
    ],
  },

  // 3. Phases & Variation
  {
    id: 'sow_phases_variation',
    tabId: 'sow',
    formTitle: 'Phases & Variation',
    formDescription: 'SOW project phases with dates, and any variation notes from General Info.',
    pdfTitle: 'Project Phases',
    pdfDescription: 'SOW phases with timeline and variation details.',
    highLevelSummary: 'Project phases with dates plus any SOW variation notes.',
    groups: [
      {
        id: 'sow_phases_dates_group',
        tabId: 'sow',
        sectionId: 'sow_phases_variation',
        formTitle: 'Project Phases',
        pdfTitle: 'Project Phases',
        pdfDescription: 'Phases with start/end dates and deliverables.',
      },
    ],
    fields: [],
  },

  // 4. RACI Chart
  {
    id: 'sow_raci_chart',
    tabId: 'sow',
    formTitle: 'RACI Chart',
    formDescription: 'Responsibility assignment matrix. R=Responsible, A=Accountable, C=Consulted, I=Informed.',
    pdfTitle: 'RACI Chart',
    pdfDescription: 'RACI responsibility assignment matrix.',
    highLevelSummary: 'RACI chart for task responsibility assignments.',
    groups: [
      {
        id: 'sow_raci_matrix_group',
        tabId: 'sow',
        sectionId: 'sow_raci_chart',
        formTitle: 'RACI Matrix',
        pdfTitle: 'RACI Matrix',
        pdfDescription: 'Task responsibility assignments.',
      },
    ],
    fields: [],
  },

  // 5. Gantt Chart
  {
    id: 'sow_gantt_chart',
    tabId: 'sow',
    formTitle: 'Gantt Chart',
    formDescription: 'Project timeline with tasks, responsible persons, and party assignments.',
    pdfTitle: 'Project Timeline',
    pdfDescription: 'Detailed project timeline with tasks and assignments.',
    highLevelSummary: 'Gantt chart and project timeline details.',
    groups: [
      {
        id: 'sow_gantt_tasks_group',
        tabId: 'sow',
        sectionId: 'sow_gantt_chart',
        formTitle: 'Gantt Tasks',
        pdfTitle: 'Gantt Tasks',
        pdfDescription: 'Project tasks with timelines and assignments.',
      },
    ],
    fields: [],
  },

  // 6. Key Assumptions
  {
    id: 'sow_key_assumptions',
    tabId: 'sow',
    formTitle: 'Key Assumptions',
    formDescription: 'Free-text list of key assumptions that underpin this SOW.',
    pdfTitle: 'Key Assumptions',
    pdfDescription: 'Key assumptions that underpin this SOW.',
    highLevelSummary: 'Key assumptions for the SOW.',
    groups: [
      {
        id: 'sow_key_assumptions_group',
        tabId: 'sow',
        sectionId: 'sow_key_assumptions',
        formTitle: 'Key Assumptions',
        pdfTitle: 'Key Assumptions',
        pdfDescription: 'Key assumptions for the project.',
      },
    ],
    fields: [
      {
        id: 'sow_key_assumptions_content',
        tabId: 'sow',
        sectionId: 'sow_key_assumptions',
        groupId: 'sow_key_assumptions_group',
        path: 'sowConfig.keyAssumptions',
        valueType: 'textarea',
        formLabel: 'Key Assumptions',
        formDescription: 'List any key assumptions specific to this SOW.',
        placeholder: 'e.g., Client will provide timely access to test environments; integration endpoints are stable...',
        pdfLabel: 'Key Assumptions',
        pdfDescription: 'Key assumptions for the SOW.',
        contributesToCompletion: false,
      },
    ],
  },

  // 7. Additional Notes
  {
    id: 'sow_additional_notes',
    tabId: 'sow',
    formTitle: 'Additional Notes',
    formDescription: 'Add any additional notes or information for the SOW.',
    pdfTitle: 'Additional Notes',
    pdfDescription: 'Supplementary notes and information.',
    highLevelSummary: 'Additional SOW notes and information.',
    groups: [],
    fields: [
      {
        id: 'sow_notes',
        tabId: 'sow',
        sectionId: 'sow_additional_notes',
        path: 'sowConfig.notes',
        valueType: 'textarea',
        formLabel: 'Additional Notes',
        formDescription: 'Add any additional notes, clarifications, or special terms for the SOW.',
        placeholder: 'e.g., Special pricing arrangements, dependencies on client actions, assumptions...',
        pdfLabel: 'Additional Notes',
        pdfDescription: 'Supplementary notes.',
        contributesToCompletion: false,
      },
    ],
  },
]
